import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { links } from '../model/links';
import { useFooterConfig, type FooterConfig } from '../providers/FooterContext';
import { useWizardSteps } from '../providers/WizardStepsContext';

export const findStepFromLocation = (path: string) => {
	const matchedStep = links.find((link) => path.includes(`/${link.key}`));
	if (matchedStep) {
		return matchedStep.key;
	}

	return 'general';
};

export const useWizard = ({
	onCancel,

	onSubmit,
	shouldResubmit = false,
	isValid = true,
	isDirty = false,
	isLoading = false,
	isEnabled = true,
	isBackNavigationEnabled = false,
}: FooterConfig) => {
	const { setConfig } = useFooterConfig();
	const onSubmitRef = useRef(onSubmit);
	const onCancelRef = useRef(onCancel);

	const { setIsLocked } = useWizardSteps();

	useEffect(() => {
		onSubmitRef.current = onSubmit;
	}, [onSubmit]);

	useEffect(() => {
		onCancelRef.current = onCancel;
	}, [onCancel]);

	useEffect(() => {
		setIsLocked((shouldResubmit && isDirty) || isLoading);
	}, [shouldResubmit, isDirty, isLoading, setIsLocked]);

	useEffect(() => {
		setConfig({
			shouldResubmit,
			isDirty,
			isLoading,
			isValid,
			isEnabled,
			isBackNavigationEnabled,
			onCancel: () => {
				onCancelRef.current?.();
			},
			onSubmit: async () => {
				await onSubmitRef.current?.();
			},
		});

		return () => {
			setConfig(undefined);
		};
	}, [
		isDirty,
		isLoading,
		isValid,
		isEnabled,
		setConfig,
		shouldResubmit,
		isBackNavigationEnabled,
	]);
};

export const useWizardNavigation = () => {
	const { stepState, setStepState } = useWizardSteps();
	const location = useLocation();
	const navigate = useNavigate();

	return useMemo(() => {
		const stepKey = findStepFromLocation(location.pathname);
		const step = links.find((link) => link.key === stepKey);

		const moveNext = () => {
			if (!step) return;
			const currentIndex = links.findIndex((link) => link.key === step.key);
			const nextStep = links[currentIndex + 1];
			if (nextStep) {
				setStepState((prev) => ({
					...prev,
					[step.key]: {
						...prev[step.key],
						isVisited: true,
					},
					[nextStep.key]: {
						...prev[nextStep.key],
						isValid: true,
					},
				}));

				navigate(`${nextStep.key}`, { replace: true });
			}
		};
		const moveBack = () => {
			if (!step) return;
			const currentIndex = links.findIndex((link) => link.key === step.key);
			const prevStep = links[currentIndex - 1];
			if (prevStep) {
				navigate(`${prevStep.key}`, { replace: true });
			}
		};

		const findPreviousStep = () => {
			if (!step) return null;
			const currentIndex = links.findIndex((link) => link.key === step.key);
			const prevStep = links[currentIndex - 1];
			if (prevStep) {
				return prevStep;
			}
			return null;
		};
		const findNextStep = () => {
			if (!step) return null;
			const currentIndex = links.findIndex((link) => link.key === step.key);
			const nextStep = links[currentIndex + 1];
			if (nextStep) {
				return nextStep;
			}
			return null;
		};

		const canGoBack = () => {
			if (!step) return false;
			const currentIndex = links.findIndex((link) => link.key === step.key);
			return currentIndex > 0;
		};

		const isStepVisited = (stepKey: keyof typeof stepState) => {
			return stepState[stepKey]?.isVisited;
		};

		const isLastStep = () => {
			if (!step) return false;
			const currentIndex = links.findIndex((link) => link.key === step.key);
			return currentIndex === links.length - 1;
		};

		return {
			currentStep: step,
			moveNext,
			moveBack,
			canGoBack,
			isStepVisited,
			findPreviousStep,
			isLastStep,
			findNextStep,
		};
	}, [location.pathname, navigate, setStepState, stepState]);
};
